<template>
  <div
    class="leaf-node"
  >
    <div class="_blocks">
      <div
        v-if="!hideOp"
        class="_op _block"
      >
        <m-select
          v-model:value="selectedOp"
          :items="opItems"
          :read-only="readOnly || !canSelectOp"
          :disabled="disabled"
          :hide-border="readOnly || !canSelectOp"
          :placeholder="$t('leafNode.placeholder')"
        />
      </div>
      <div class="_property _block">
        <m-select
          v-model:value="selectedScopeItem"
          :items="scopeItems"
          return-object
          :read-only="readOnly"
          :disabled="disabled"
          full-width
        />
      </div>
      <div class="_scope _block">
        <number-scope
          v-if="show(propertyType.number)"
          :key="selectedScopeItem.value"
          :value="scope"
          :is-not="isNot"
          :read-only="readOnly"
          :disabled="disabled"
          :hide-op="hideLeafOp"
          :hide-is-empty="hideIsEmpty"
          :number-format="propertyNumberFormat"
          @change="updateScope"
          @change-not="updateNotOperator"
        />
        <date-scope
          v-if="show(propertyType.date)"
          :key="selectedScopeItem.value"
          :value="scope"
          :read-only="readOnly"
          :disabled="disabled"
          :hide-op="hideLeafOp"
          :has-dynamic-values="hasDynamicDates"
          :hide-is-empty="hideIsEmpty"
          @change="updateScope"
          @change-not="updateNotOperator"
        />
        <space-scope
          v-if="show(propertyType.space)"
          :key="selectedScopeItem.value"
          :value="scope"
          :is-not="isNot"
          :read-only="readOnly"
          :disabled="disabled"
          :hide-op="hideLeafOp"
          :hide-is-empty="hideIsEmpty"
          :max-tags="maxTags"
          @change="updateScope"
          @change-not="updateNotOperator"
        />
        <option-scope
          v-if="show(propertyType.options, propertyType.singleSelect)"
          :key="selectedScopeItem.value"
          :value="scope"
          :read-only="readOnly"
          :disabled="disabled"
          :hide-op="hideLeafOp"
          :is-not="isNot"
          :options="options"
          :hide-is-empty="hideIsEmpty"
          :max-tag-text-length="maxTagTextLength"
          :max-tags="maxTags"
          @change="updateScope"
          @change-not="updateNotOperator"
        />
        <status-scope
          v-if="show(propertyType.status)"
          :key="selectedScopeItem.value"
          :value="scope"
          :read-only="readOnly"
          :disabled="disabled"
          :hide-op="hideLeafOp"
          :is-not="isNot"
          :max-tag-text-length="maxTagTextLength"
          :max-tags="maxTags"
          @change="updateScope"
          @change-not="updateNotOperator"
        />
        <user-scope
          v-if="show(propertyType.user)"
          :key="selectedScopeItem.value"
          :value="scope"
          :read-only="readOnly"
          :disabled="disabled"
          :hide-op="hideLeafOp"
          :is-not="isNot"
          :max-tag-text-length="maxTagTextLength"
          :hide-is-empty="hideIsEmpty"
          :field-name="userScopeConfig.edges.users"
          :max-tags="maxTags"
          @change="updateScope"
          @change-not="updateNotOperator"
        />
        <user-scope
          v-if="show(userScopeType.staticUsers)"
          :key="selectedScopeItem.value"
          :value="scope"
          :read-only="readOnly"
          :disabled="disabled"
          :hide-op="hideLeafOp"
          :is-not="isNot"
          :max-tag-text-length="maxTagTextLength"
          :hide-is-empty="hideIsEmpty"
          :field-name="userScopeConfig.edges.staticUsers"
          :max-tags="maxTags"
          @change="updateScope"
          @change-not="updateNotOperator"
        />
        <goal-scope
          v-if="show(GOAL_RELATION, GOAL_SELF)"
          :key="selectedScopeItem.value"
          :value="scope"
          :read-only="readOnly"
          :disabled="disabled"
          :hide-op="hideLeafOp"
          :is-not="isNot"
          :hide-is-empty="hideIsEmpty || show(GOAL_SELF)"
          :max-tags="maxTags"
          @change="updateScope"
          @change-not="updateNotOperator"
        />
        <lookup-scope
          v-if="show(propertyType.lookup)"
          :key="selectedScopeItem.value"
          :value="scope"
          :read-only="readOnly"
          :disabled="disabled"
          :hide-op="hideLeafOp"
          :is-not="isNot"
          :max-tag-text-length="maxTagTextLength"
          :property="selectedScopeItem.property"
          :options="options"
          :max-tags="maxTags"
          @change="updateScope"
          @change-not="updateNotOperator"
        />
      </div>
    </div>
    <div
      v-if="canDelete && !readOnly && !disabled"
      class="_more"
    >
      <m-dropdown
        :title="$t('general.actions')"
      >
        <m-btn
          icon="ellipsis"
          data-cy="leaf-node-more"
          hide-border
          fab
          light
        />
        <template #overlay>
          <m-card
            no-padding
            list
          >
            <m-card-item
              data-cy="delete-filter"
              icon="delete"
              @click="$emit('delete')"
            >
              {{ $t('userScopeFilter.remove') }}
            </m-card-item>
            <m-card-item
              icon="copy"
              @click="$emit('duplicate')"
            >
              {{ $t('general.duplicate') }}
            </m-card-item>
            <m-card-item
              v-if="allowGrouping"
              icon="wrap"
              @click="$emit('group')"
            >
              {{ $t('userScopeFilter.group') }}
            </m-card-item>
          </m-card>
        </template>
      </m-dropdown>
    </div>
  </div>
</template>

<script>
import DateScope from '@/components/filter/DateScope.vue';
import GoalScope from '@/components/filter/GoalScope.vue';
import LookupScope from '@/components/filter/LookupScope.vue';
import NumberScope from '@/components/filter/NumberScope.vue';
import OptionScope from '@/components/filter/OptionScope.vue';
import SpaceScope from '@/components/filter/SpaceScope.vue';
import StatusScope from '@/components/filter/StatusScope.vue';
import UserScope from '@/components/filter/UserScope.vue';
import useDebounce from 'shared/composables/debounce';
import useProperties from '@/composables/property/property';
import useScopeItem from '@/composables/filter/scope-item';
import { GOAL_RELATION, GOAL_SELF } from '@/lib/props/custom-types';
import { copy } from 'shared/lib/copy';
import { numberFormat, propertyType, userScopeOperator, userScopeType } from 'shared/constants.json';
import { userScope as userScopeConfig } from 'shared/api/query/configs.json';

export default {
  name: 'LeafNode',
  props: {
    // A node (parent) is expected to have exactly one child, the childNode.
    // The parent node is used to set the not-operator
    node: {
      type: Object,
      required: true,
    },
    op: {
      type: String,
      default: '',
    },
    canSelectOp: {
      type: Boolean,
      default: false,
    },
    hideOp: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
    hideLeafOp: {
      type: Boolean,
      default: false,
    },
    scopeItems: {
      type: Array,
      default: () => [],
    },
    maxTagTextLength: {
      type: Number,
      default: 0,
    },
    hasDynamicDates: {
      type: Boolean,
      default: false,
    },
    multiModel: {
      type: Boolean,
      default: false,
    },
    allowGrouping: {
      type: Boolean,
      default: false,
    },
    maxTags: {
      type: Number,
      default: 2,
    },
  },

  setup() {
    const { debounce } = useDebounce();
    const { properties } = useProperties();
    const { selectedScopeItem: getSelectedScopeItem, scopeFromScopeItem, selectedScopePropertyType: getSelectedScopePropertyType, selectedScopeType: getSelectedScopeType } = useScopeItem();

    return { debounce, properties, getSelectedScopeItem, getSelectedScopeType, scopeFromScopeItem, getSelectedScopePropertyType };
  },
  emits: ['delete', 'change', 'change-operator', 'duplicate', 'group'],
  components: { SpaceScope, GoalScope, UserScope, OptionScope, DateScope, NumberScope, StatusScope, LookupScope },
  data() {
    return {
      localNode: null,
      propertyType,
      opItems: [
        {
          text: this.$t('general.and'),
          value: userScopeOperator.and,
        },
        {
          text: this.$t('general.or'),
          value: userScopeOperator.or,
        },
      ],
      userScopeType,
      GOAL_RELATION,
      GOAL_SELF,
      userScopeConfig,
    };
  },
  computed: {
    scope() {
      return this.localNode.children[0].scope;
    },
    isNot() {
      return this.localNode.op === userScopeOperator.not;
    },
    selectedScopeType() {
      return this.getSelectedScopeType(this.scope);
    },
    selectedScopeItem: {
      get() {
        return this.getSelectedScopeItem(this.scopeItems, this.scope);
      },
      set(val) {
        this.updateScope(this.scopeFromScopeItem(val));
      },
    },
    selectedOp: {
      get() {
        return this.op;
      },
      set(val) {
        if (this.op === val) {
          return;
        }
        this.$emit('change-operator', val);
      },
    },
    propertyNumberFormat() {
      if (this.selectedScopeType === userScopeType.directProperty) {
        return numberFormat.number;
      }
      return this.selectedScopeItem.property.numberFormat;
    },
    selectedType() {
      return this.getSelectedScopePropertyType(this.selectedScopeItem);
    },
    hideIsEmpty() {
      switch (this.selectedScopeType) {
        case userScopeType.staticUsers:
          return true;
        case userScopeType.directProperty:
          return this.selectedScopeItem.directProperty.noIsEmptyFilter;
        case userScopeType.property:
        default:
          return false;
      }
    },
    options() {
      if (this.selectedType === undefined) {
        return [];
      }
      if (this.selectedType === propertyType.lookup) {
        return this.properties.filter((p) => p.uid === this.selectedScopeItem.property.lookupProperty.uid)[0].options;
      }
      if (![propertyType.options, propertyType.singleSelect].includes(this.selectedType)) {
        return [];
      }
      return this.properties.filter((p) => p.uid === this.selectedScopeItem.property.uid)[0].options;
    },
  },
  methods: {
    show(...types) {
      return types.some((type) => {
        switch (this.selectedScopeType) {
          case userScopeType.staticUsers:
            return type === userScopeType.staticUsers;
          case userScopeType.property:
          case userScopeType.directProperty:
            return type === this.selectedType;
          default:
            return false;
        }
      });
    },
    updateScope(scope) {
      if (JSON.stringify(this.scope) === JSON.stringify(scope)) {
        return;
      }
      this.localNode.children[0] = { ...this.localNode.children[0], scope };
    },
    updateNotOperator(op) {
      if (op === this.localNode.op) {
        return;
      }
      this.localNode = { ...this.localNode, op };
    },
  },
  watch: {
    node(newVal) {
      if (JSON.stringify(this.localNode) === JSON.stringify(newVal)) {
        return;
      }
      this.localNode = copy(newVal);
    },
    localNode: {
      handler(newVal) {
        this.debounce(() => this.$emit('change', newVal), 5);
      },
      deep: true,
    },
  },
  created() {
    this.localNode = this.node;
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .leaf-node {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: inherit;
    gap: .8rem;

    ._blocks {
      display: flex;
      width: inherit;

      ._op {
        width: 7rem;
        display: flex;
        align-items: flex-start;
        justify-content: flex-end;
      }

      ._block {
        &:not(:last-of-type) {
          padding-right: .8rem;
        }
      }

      ._scope {
        width: inherit;
        max-width: 60rem;
      }
    }
  }
</style>
